import { AsyncPipe, CommonModule, NgForOf, NgIf } from "@angular/common";
import { ChangeDetectionStrategy, Component } from "@angular/core";
import { getHttpErrorMessage } from "../../../shared/utils/http-error.util";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { RouterLink } from "@angular/router";
import { TuiValidationError } from "@taiga-ui/cdk";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDropdown,
  TuiError,
  TuiHint,
  TuiLink,
  TuiTextfield,
  TuiLoader,
} from "@taiga-ui/core";
import {
  TuiBreadcrumbs,
  TuiDataListWrapper,
  TuiFade,
  TuiFieldErrorPipe,
  TuiFiles,
  tuiFilesAccepted,
  TuiFilterByInputPipe,
  TuiStringifyContentPipe,
  TuiSwitch,
  TuiTabs,
  tuiValidationErrorsProvider,
} from "@taiga-ui/kit";
import { TuiNavigation } from "@taiga-ui/layout";
import {
  TuiComboBoxModule,
  TuiInputModule,
  TuiInputPhoneModule,
  TuiMultiSelectModule,
  TuiSelectModule,
  TuiTextareaModule,
  TuiTextfieldControllerModule,
} from "@taiga-ui/legacy";
import { catchError, map, of } from "rxjs";
import { AdminService } from "../../../core/services/admin.service";
import { FileService } from "../../../core/services/file.service";
import { RolesService } from "../../../core/services/roles.service";
import { Breadcrumb } from "../../../model/interface/breadcrumb";
import { flush } from "@angular/core/testing";
import { DisableUnlessPermissionDirective } from "../../../core/directives/disable-unless-permission.directive";
import { EnterSubmitDirective } from "../../../core/directives/enter-submit.directive";
import {
  nameValidator,
  phoneValidator,
} from "../../../shared/validators/form-validators";

@Component({
  selector: "app-add-user",
  imports: [
    DisableUnlessPermissionDirective,
    EnterSubmitDirective,
    FormsModule,
    CommonModule,
    NgForOf,
    NgIf,
      TuiLoader,
    TuiAppearance,
    TuiBreadcrumbs,
    TuiButton,
    TuiDataList,
    TuiDropdown,
    TuiFade,
    TuiHint,
    TuiLink,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiFiles,
    TuiFieldErrorPipe,
    TuiError,
    ReactiveFormsModule,
    AsyncPipe,
    TuiInputModule,
    TuiTextfieldControllerModule,
    TuiTextareaModule,
    TuiInputPhoneModule,
    TuiComboBoxModule,
    TuiMultiSelectModule,
    TuiSelectModule,
    TuiDataListWrapper,
    TuiFilterByInputPipe,
    TuiStringifyContentPipe,
    TuiSwitch,
    RouterLink,
  ],
  templateUrl: "./add-user.component.html",
  styleUrl: "./add-user.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    tuiValidationErrorsProvider({
      duplicateEmail: "Already existing!",
    }),
  ],
})
export class AddUserComponent {
  protected readonly breadcrumbs: Breadcrumb[] = [
    { label: "Royal Hospital" },
    { label: "Users", link: "/admin/users" },
    { label: "Add New User" },
  ];
  protected value = false;
  userId: string | null = null;
  imageEditable: boolean = false;
  userForm: FormGroup;
  isLoading: boolean = false; 
  isSaving: boolean = false; 
  private isUserDataLoading = false;
  private isRolesLoading = false;
  newUser: boolean = true;
  image: string | null = null;
  roles: any[] = [];
  readonly roleSelectHint =
    "Select SUPER ADMIN role to grant report access, and select Manager when adding other users.";
  /**
   * Cached role value coming from the loaded user, kept around so we can
   * resolve it to the actual reference from the `roles` list once that
   * list arrives. Without this, the combo box has nothing to display in
   * edit mode (since tui-combo-box matches by reference).
   */
  private pendingUserRole: any = null;
  imagePreview: string | ArrayBuffer | null = null;
  stringify = (role: any) => role?.name ?? "";
  /**
   * tui-combo-box matches selected value with list items by reference.
   * When editing, the user's `role` may be a string ObjectId (the
   * single-user endpoint doesn't populate) or a populated role object
   * from another source — either way it's a different reference than
   * the items in `roles`. Compare by `_id` so the combo can highlight
   * the correct option.
   */
  protected readonly roleIdentityMatcher = (a: any, b: any): boolean => {
    if (!a || !b) return false;
    const aId = typeof a === "string" ? a : a?._id ?? a?.id;
    const bId = typeof b === "string" ? b : b?._id ?? b?.id;
    return !!aId && !!bId && String(aId) === String(bId);
  };

  constructor(
    private fb: FormBuilder,
    private adminservice: AdminService,
    private router: Router,
    private alerts: TuiAlertService,
    private route: ActivatedRoute,
    private filesService: FileService,
    private rolesService: RolesService
  ) {
    this.userForm = this.fb.group({
      _id: [null],
      name: [
        "",
        [Validators.required, Validators.maxLength(70), nameValidator],
      ],
      email: ["", [Validators.required, Validators.email]],
      contactNo: [
        "",
        [Validators.required, Validators.maxLength(20), phoneValidator],
      ],
      active: [true],
      // Profile photo is recommended (backend defaults to "default.jpg") so
      // we no longer block save on it; the upload UI still works.
      profilePic: [""],
      role: "",
      notes: [""],
    });
  }

  navigate(link?: string): void {
    if (link) {
      this.router.navigate([link]);
    }
  }

  getProfilePicError() {
    const control = this.userForm.get("profilePic");
    if (control?.hasError("required")) return "Profile Picture is required.";
    return null;
  }

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      this.userId = params.get("id");
      if (this.userId) {
        this.loadUserData(this.userId);
        this.userForm.get("email")?.disable();
        this.newUser = false;
        this.imageEditable = true;
      }
    });
    this.loadRoles();
  }

  editImage() {
    this.imageEditable = false;
  }

  loadUserData(userId: string): void {
    this.isUserDataLoading = true;
    this.isLoading = true;
    this.adminservice
      .getOneUserById(userId)
      .pipe(
        catchError((error) => {
          console.error("Error loading user data:", error);
          this.isUserDataLoading = false;
          this.checkLoadingState();
          return of(null);
        })
      )
      .subscribe((response: { profilePic?: string; role?: any } | null) => {
        if (response) {
          this.pendingUserRole = response.role ?? null;
          this.userForm.patchValue(response);
          this.imagePreview = response.profilePic ?? null;
          this.resolvePendingRole();
        }
        this.isUserDataLoading = false;
        this.checkLoadingState();
      });
  }

  loadRoles(): void {
    this.isRolesLoading = true;
    this.isLoading = true;
    this.rolesService.getRolesForOptions().subscribe(
      (response) => {
        const list = (response as any[]) ?? [];
        this.roles = [...list].sort((a, b) => {
          const aIsSuperAdmin = (a?.name ?? "").toUpperCase() === "SUPER ADMIN";
          const bIsSuperAdmin = (b?.name ?? "").toUpperCase() === "SUPER ADMIN";
          if (aIsSuperAdmin && !bIsSuperAdmin) return -1;
          if (!aIsSuperAdmin && bIsSuperAdmin) return 1;
          return 0;
        });
        this.resolvePendingRole();
        this.isRolesLoading = false;
        this.checkLoadingState()
      },
      (error) => {
        console.error("Error fetching roles:", error);
        this.isRolesLoading = false;
        this.checkLoadingState()
      }
    );
  }

  private checkLoadingState(): void {
    this.isLoading = this.isUserDataLoading || this.isRolesLoading;
  }

  /**
   * Match the user's role (possibly an id string or a populated object)
   * against the loaded `roles` list so the combo box can display it.
   */
  private resolvePendingRole(): void {
    if (!this.pendingUserRole || !this.roles?.length) return;
    const raw = this.pendingUserRole;
    const targetId =
      typeof raw === "string" ? raw : raw?._id ?? raw?.id ?? null;
    if (!targetId) return;
    const match = this.roles.find(
      (r) => String(r?._id ?? r?.id) === String(targetId)
    );
    if (match) {
      this.userForm.get("role")?.setValue(match);
      this.pendingUserRole = null;
    }
  }

  saveUser() {
    this.isSaving = true;
    if (this.userForm.valid) {
      const payload = this.userForm.getRawValue();
      payload.role = this.extractRoleId(payload.role);
      if (this.userId) {
        this.adminservice
          .updateUser(payload)
          .pipe(
            catchError((error) => {
              if (getHttpErrorMessage(error) === "Email is already existing!") {
                this.userForm
                  .get("email")
                  ?.setErrors({ duplicateEmail: true });
              }
              if (
                getHttpErrorMessage(error) === "Contact Number is already existing!"
              ) {
                this.userForm
                  .get("contactNo")
                  ?.setErrors({ duplicateEmail: true });
              }
              this.isSaving = false;
              this.alerts
                .open(getHttpErrorMessage(error), {
                  label: "Error",
                  appearance: "negative",
                })
                .subscribe();
              return of(null);
            })
          )
          .subscribe((res: any) => {
            this.isSaving = false;
            if (res) {
              this.alerts
                .open("User updated successfully", {
                  label: "Success",
                  appearance: "success",
                })
                .subscribe();
              this.router.navigateByUrl("/admin/users");
            }
          });
      } else {
        const createPayload = {
          ...this.userForm.value,
          role: this.extractRoleId(this.userForm.value.role),
        };
        this.adminservice
          .createUser(createPayload)
          .pipe(
            catchError((error) => {
              if (getHttpErrorMessage(error) === "Email is already existing!") {
                this.userForm
                  .get("email")
                  ?.setErrors({ duplicateEmail: true });
              }
              if (
                getHttpErrorMessage(error) === "Contact Number is already existing!"
              ) {
                this.userForm
                  .get("contactNo")
                  ?.setErrors({ duplicateEmail: true });
              }
              this.isSaving = false;
              this.alerts
                .open(getHttpErrorMessage(error), {
                  label: "Error",
                  appearance: "negative",
                })
                .subscribe();
              return of(null);
            })
          )
          .subscribe((res: any) => {
            this.isSaving = false;
            if (res) {
              this.alerts
                .open("User added successfully", {
                  label: "Success",
                  appearance: "success",
                })
                .subscribe();
              this.router.navigateByUrl("/admin/users");
            }
          });
      }
    } else {
      this.userForm.markAllAsTouched();
      this.isSaving = false;
    }
  }

  private extractRoleId(role: any): string | null {
    if (!role) return null;
    if (typeof role === "string") return role;
    return role?._id ?? role?.id ?? null;
  }

  // File Upload
  private generatePreview(file: File): void {
    this.imagePreview = URL.createObjectURL(file);
  }

  protected readonly control = new FormControl<File[]>([], [maxFilesLength(1)]);
  protected readonly accepted$ = this.control.valueChanges.pipe(
    map((files) => {
      let acceptedFiles = tuiFilesAccepted(this.control);

      if (acceptedFiles.length > 1) {
        acceptedFiles = [acceptedFiles[acceptedFiles.length - 1]];
        this.control.setValue(acceptedFiles, { emitEvent: false });
      }

      if (acceptedFiles.length > 0) {
        this.uploadImage(acceptedFiles[0]);
      }
      return acceptedFiles;
    })
  );

  protected rejected: readonly File[] = [];

  protected onReject(files: readonly File[]): void {
    this.rejected = Array.from(new Set(this.rejected.concat(files)));
  }

  protected onRemove(file: File): void {
    this.rejected = this.rejected.filter((rejected) => rejected !== file);
    const updatedFiles =
      this.control.value?.filter((current) => current !== file) ?? [];
    this.control.setValue(updatedFiles);
    if (updatedFiles.length === 0) {
      this.imagePreview = null;
    }
  }

  private uploadImage(file: File): void {
    this.generatePreview(file);

    this.isLoading = true;
    this.filesService.postFile(file, "USER_IMAGE_UPLOAD").subscribe(
      (res) => {
        this.isLoading = false;
        const data = res as any;
        this.image = data.url;
        this.userForm.get("profilePic")?.patchValue(this.image);
        this.filesService.fileUploadingSubject.next({
          type: "USER_IMAGE_UPLOAD",
          loading: false,
        });
      },
      (error) => {
        this.isLoading = false;
        this.alerts
          .open(getHttpErrorMessage(error), {
            label: "Error",
            appearance: "negative",
          })
          .subscribe();
      }
    );
  }
}

export function maxFilesLength(maxLength: number): ValidatorFn {
  return ({ value }: AbstractControl) =>
    value.length > maxLength
      ? {
          maxLength: new TuiValidationError(
            "Error: maximum limit - 1 file for upload"
          ),
        }
      : null;
}
